"use client";

import type { InfiniteData } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import type { Fax, FaxListItem } from "@/types/fax";
import type { CursorPage } from "@/types/pagination";
import { faxesQueryKeys } from "@/api/provider/fax/faxes-query";
const FAX_ALERT_TYPES = [AlertType.FAX_DELIVERED, AlertType.FAX_FAILED, AlertType.FAX_RECEIVED, AlertType.FAX_UPDATE, AlertType.FAX_ANALYSIS_UPDATE];
const isFaxListItem = (value: unknown): value is FaxListItem => {
  return typeof value === "object" && value !== null && "id" in value && "status" in value && "direction" in value && typeof (value as {
    id: unknown;
  }).id === "string";
};
interface FaxStateUpdate {
  id: string;
  readDate: string | null;
}
const isFaxStateUpdate = (value: unknown): value is FaxStateUpdate => {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as {
    id?: unknown;
    readDate?: unknown;
  };
  return typeof obj.id === "string" && (obj.readDate === null || typeof obj.readDate === "string");
};
const isFaxAnalysisUpdate = (value: unknown): value is {
  id: string;
} => {
  return typeof value === "object" && value !== null && "id" in value && typeof (value as {
    id: unknown;
  }).id === "string";
};

/**
 * Mirrors {@link import('./use-chat-realtime-cache').useChatRealtimeCache} for
 * fax. Subscribes to:
 *  - FAX_DELIVERED / FAX_FAILED (outbound status transitions): surgically
 *    patches the detail cache + every cached list row in place.
 *  - FAX_RECEIVED (inbound): prepends a new row to every list page that
 *    matches the alert's direction filter, mirroring
 *    {@link import('@/query/comms/chat').prependNewChatToInfiniteLists},
 *    and bumps the sidebar unread count.
 *
 * The notification-feed cache (bell badge) is invalidated globally by
 * ``useInAppNotifications`` for every persisted alert — no extra wiring here.
 */
export function useFaxRealtimeCache() {
  useWebSocketCallbackSubscription(FAX_ALERT_TYPES, handleFaxAlert, true, "useFaxRealtimeCache");
}
function handleFaxAlert(message: AlertDetail) {
  const payload = message.data;
  if (message.type === AlertType.FAX_UPDATE) {
    if (!isFaxStateUpdate(payload)) return;
    applyFaxStateUpdate(payload);
    return;
  }
  if (message.type === AlertType.FAX_ANALYSIS_UPDATE) {
    if (!isFaxAnalysisUpdate(payload)) return;
    // The payload is {id} only; the real analysis data (extraction JSON +
    // suggested patient/provider/template relationships) is too rich to
    // reconstruct client-side, so refetch and reuse the detail serializer.
    queryClient.invalidateQueries({
      queryKey: faxesQueryKeys.detail(payload.id)
    });
    return;
  }
  if (!isFaxListItem(payload)) return;
  if (message.type === AlertType.FAX_RECEIVED) {
    prependFaxToLists(payload);
    incrementUnreadCount();
    return;
  }
  applyFaxUpdate(payload);
}
function prependFaxToLists(incoming: FaxListItem) {
  const listCaches = queryClient.getQueriesData<InfiniteData<CursorPage<FaxListItem>>>({
    queryKey: [...faxesQueryKeys.all, "list"]
  });
  for (const [queryKey, data] of listCaches) {
    if (!data) continue;

    // Filters are stored as the 3rd segment of the query key (see
    // ``faxesQueryKeys.list``). A list filtered to OUTBOUND should not
    // accept an INBOUND prepend; ALL / undefined / matching direction does.
    // Likewise a list filtered to a specific fax line should not accept a
    // prepend from a different line — the sidebar line picker writes
    // ``phoneNumberId`` into the filter object.
    const filters = queryKey[2] as {
      direction?: string;
      phoneNumberId?: string;
    } | undefined;
    if (filters?.direction && filters.direction !== incoming.direction) {
      continue;
    }
    if (filters?.phoneNumberId && filters.phoneNumberId !== incoming.phoneNumberId) {
      continue;
    }

    // Skip if already present (e.g. webhook redelivery raced the cache).
    const exists = data.pages.some(page => (page?.data ?? []).some(row => row.id === incoming.id));
    if (exists) continue;
    const firstPage = data.pages[0];
    if (!firstPage) continue;
    queryClient.setQueryData<InfiniteData<CursorPage<FaxListItem>>>(queryKey, {
      ...data,
      pages: [{
        ...firstPage,
        data: [incoming, ...firstPage.data],
        total: (firstPage.total ?? 0) + 1
      }, ...data.pages.slice(1)]
    });
  }
}

// Patch, not invalidate: a refetch right after the webhook commit can hit a lagging replica.
function incrementUnreadCount() {
  queryClient.setQueryData<number | null>(faxesQueryKeys.unreadCount(), old => old == null ? old : old + 1);
}
function applyFaxUpdate(update: FaxListItem) {
  // Merge into the detail cache only when it already exists. The alert
  // payload is intentionally compact and omits attachments / pdfPath /
  // errorCode / errorMessage / signalwireFaxSid — overwriting an existing
  // Fax with this stripped shape would wipe the PDF iframe and failure
  // banner.
  queryClient.setQueryData<Fax | null>(faxesQueryKeys.detail(update.id), old => old ? {
    ...old,
    status: update.status,
    numPages: update.numPages ?? old.numPages,
    sentAt: update.sentAt ?? old.sentAt,
    completedAt: update.completedAt ?? old.completedAt,
    faxContact: update.faxContact ?? old.faxContact
  } : old);

  // Walk every cached list cache (filters vary per call site) and patch the
  // row in place. Surgical update only — mirrors the chat realtime pattern,
  // skips a network round-trip on every transition.
  const listCaches = queryClient.getQueriesData<InfiniteData<CursorPage<FaxListItem>>>({
    queryKey: [...faxesQueryKeys.all, "list"]
  });
  for (const [queryKey, data] of listCaches) {
    if (!data) continue;
    let hit = false;
    const nextPages = data.pages.map(page => {
      if (!page) return page;
      let pageHit = false;
      const nextData = page.data.map(row => {
        if (row.id !== update.id) return row;
        pageHit = true;
        return {
          ...row,
          status: update.status,
          numPages: update.numPages ?? row.numPages,
          sentAt: update.sentAt ?? row.sentAt,
          completedAt: update.completedAt ?? row.completedAt,
          faxContact: update.faxContact ?? row.faxContact
        };
      });
      if (!pageHit) return page;
      hit = true;
      return {
        ...page,
        data: nextData
      };
    });
    if (!hit) continue;
    queryClient.setQueryData<InfiniteData<CursorPage<FaxListItem>>>(queryKey, {
      ...data,
      pages: nextPages
    });
  }
}

// Coalesce unread-count refetches. A burst of FAX_UPDATE messages (e.g. a bulk
// mark-read, or simply high tenant fax volume) would otherwise invalidate — and
// refetch — the unread count once per message. Debouncing collapses the burst
// into a single refetch on the trailing edge.
let unreadCountInvalidateTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleUnreadCountInvalidation() {
  if (unreadCountInvalidateTimer) clearTimeout(unreadCountInvalidateTimer);
  unreadCountInvalidateTimer = setTimeout(() => {
    unreadCountInvalidateTimer = null;
    queryClient.invalidateQueries({
      queryKey: faxesQueryKeys.unreadCount()
    });
  }, 500);
}
function applyFaxStateUpdate(update: FaxStateUpdate) {
  queryClient.setQueryData<Fax | null>(faxesQueryKeys.detail(update.id), old => old ? {
    ...old,
    readDate: update.readDate
  } : old);
  const listCaches = queryClient.getQueriesData<InfiniteData<CursorPage<FaxListItem>>>({
    queryKey: [...faxesQueryKeys.all, "list"]
  });
  for (const [queryKey, data] of listCaches) {
    if (!data) continue;
    let hit = false;
    const nextPages = data.pages.map(page => {
      if (!page) return page;
      let pageHit = false;
      const nextData = page.data.map(row => {
        if (row.id !== update.id) return row;
        pageHit = true;
        return {
          ...row,
          readDate: update.readDate
        };
      });
      if (!pageHit) return page;
      hit = true;
      return {
        ...page,
        data: nextData
      };
    });
    if (!hit) continue;
    queryClient.setQueryData<InfiniteData<CursorPage<FaxListItem>>>(queryKey, {
      ...data,
      pages: nextPages
    });
  }

  // Mirrors use-chat-realtime-cache.ts: per-row patches clear the inline
  // "New" badge, but the sidebar menu's unread *count* is its own query and
  // would otherwise stay stale until staleTime expires. Invalidating here
  // keeps tab B's menu badge in sync when tab A flips the row.
  scheduleUnreadCountInvalidation();
}